import { Integrators, IntegratorsLabel, Shop } from "@/types/v2";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/Base/Form";
import { Input } from "@/components/Base/Input";
import { Button } from "@/components/Base/Button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/Base/Select";
import { Checkbox } from "@/components/Base/Checkbox";
import { CheckboxInput } from "@/components/Base/CheckboxInput";
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";
import { useToast } from "@/hooks/useToast";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/Base/Card";
import { Logo } from "@/components/Admin/Logo";
import { STATUS_OPTIONS } from "@/lib/const";

type ShopFormValues = Partial<Shop> &
  Pick<Shop, "name" | "integration" | "schemaVersion"> & {};

export type ShopFormProps = {
  isSubmitting?: boolean;
  onSubmit?: (values: ShopFormValues) => Promise<void> | void;
  onCancel?: () => void;
  initialData?: Shop;
  submitTitle?: string;
  cancelTitle?: string;
};

const defaultValues: ShopFormValues = {
  name: "",
  shopId: "",
  tenantId: "",
  integration: Integrators.manual,
  disabled: false,
  schemaVersion: 2,
  refreshTimeInSeconds: 30, // 30 second default
  integrationSubdomain: "",
  statusesToSync: [],
  integrationKey: "prod",
  ownerId: "",
  stripeCustomerId: "",
  stripeSubscriptionId: "",
  stripeSubscriptionStatus: "",
  logoUrl: "",
};

const createFormSchema = () => {
  const baseStringSchema = z.string();

  return z
    .object({
      name: baseStringSchema.min(1, "Shop name is required"),
      shopId: baseStringSchema.optional(),
      integration: baseStringSchema.optional(),
      schemaVersion: z.number(),
      // If integration is shopware, tenantId is required
      tenantId: baseStringSchema.optional(),
      disabled: z.boolean().optional(),
      refreshTimeInSeconds: z
        .number()
        .min(15, "Minimum refresh time is 15 seconds")
        .max(3600, "Maximum refresh time is 3600 seconds")
        .optional(),
      integrationSubdomain: baseStringSchema.optional(),
      statusesToSync: z.array(z.string()).optional(),
      integrationKey: z.enum(["dev", "prod"]).optional(),
      ownerId: baseStringSchema.optional(),
      stripeCustomerId: baseStringSchema.optional(),
      stripeSubscriptionId: baseStringSchema.optional(),
      stripeSubscriptionStatus: baseStringSchema.optional(),
      logoUrl: baseStringSchema.optional(),
    })
    .superRefine((data, ctx) => {
      // If integration is shopware, tenantId is required
      if (data.integration === Integrators.shopware) {
        if (!data.tenantId || data.tenantId.trim().length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Tenant ID is required when integration is Shop-Ware",
            path: ["tenantId"],
          });
        }
      }
      // If integration is not manual, shopId is required
      if (data.integration !== Integrators.manual) {
        if (!data.shopId || data.shopId.trim().length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Shop ID is required for this integration",
            path: ["shopId"],
          });
        }
      }
    });
};

export const ShopForm = ({
  isSubmitting,
  initialData,
  onCancel,
  onSubmit,
  cancelTitle,
  submitTitle,
}: ShopFormProps) => {
  const formSchema = createFormSchema();
  const formValues: Partial<Shop> & Pick<Shop, "id"> = {
    ...defaultValues,
    ...initialData,
    id: initialData?.id ?? "",
  };

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: formValues,
  });

  const [, copy] = useCopyToClipboard();
  const { toast } = useToast();

  const handleCopyShopId = async () => {
    const success = await copy(initialData?.id ?? "");
    if (success) {
      toast({
        title: "Shop ID Copied",
        description: "The shop ID has been copied to your clipboard.",
        variant: "success",
      });
    }
  };

  const handleCancel = () => {
    // Handle cancel action
    if (onCancel) {
      onCancel();
    }
  };

  const handleSubmit = async (data: z.infer<typeof formSchema>) => {
    // Handle form submission
    if (onSubmit) {
      await onSubmit({ ...formValues, ...data } as ShopFormValues);
    }
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="flex w-full flex-col gap-4 md:grid md:grid-cols-2"
      >
        {/* Basic Info Card */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-2">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Shop Name</FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        placeholder="Shop Name"
                        disabled={form.formState.isSubmitting || isSubmitting}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {initialData?.id && (
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <FormLabel>Shop ID</FormLabel>
                    <Input
                      type="text"
                      value={initialData.id}
                      disabled={true}
                      className="bg-muted"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleCopyShopId}
                    className="mt-6"
                  >
                    Copy Shop ID
                  </Button>
                </div>
              )}
              <FormField
                control={form.control}
                name="integration"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Integration</FormLabel>
                    <FormControl>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select an integration" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={Integrators.shopware}>
                            {IntegratorsLabel[Integrators.shopware]}
                          </SelectItem>
                          <SelectItem value={Integrators.tekmetric}>
                            {IntegratorsLabel[Integrators.tekmetric]}
                          </SelectItem>
                          <SelectItem value={Integrators.manual}>
                            {IntegratorsLabel[Integrators.manual]}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-2 gap-2">
                <FormField
                  control={form.control}
                  name="tenantId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tenant ID</FormLabel>
                      <FormControl>
                        <Input
                          type="text"
                          placeholder="Tenant ID"
                          disabled={
                            form.formState.isSubmitting ||
                            isSubmitting ||
                            form.getValues().integration !==
                              Integrators.shopware
                          }
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="shopId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Shop ID</FormLabel>
                      <FormControl>
                        <Input
                          type="text"
                          placeholder="Shop ID"
                          disabled={
                            form.formState.isSubmitting ||
                            isSubmitting ||
                            form.getValues().integration === Integrators.manual
                          }
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Integration Settings Card */}
        <Card>
          <CardHeader>
            <CardTitle>Integration Settings</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-2">
              <div className="grid grid-cols-2 gap-2">
                <FormField
                  control={form.control}
                  name="integrationKey"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Integration Key</FormLabel>
                      <FormControl>
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                          disabled={
                            form.formState.isSubmitting ||
                            isSubmitting ||
                            form.getValues().integration === Integrators.manual
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select environment" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="dev">Development</SelectItem>
                            <SelectItem value="prod">Production</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="refreshTimeInSeconds"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Refresh Time (seconds)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={15}
                          max={3600}
                          placeholder="30"
                          disabled={
                            form.formState.isSubmitting ||
                            isSubmitting ||
                            form.getValues().integration === Integrators.manual
                          }
                          {...field}
                          onChange={(e) => {
                            const value = e.target.value;
                            field.onChange(
                              value === "" ? undefined : parseInt(value)
                            );
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="integrationSubdomain"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Integration Subdomain</FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        placeholder="Subdomain"
                        disabled={
                          form.formState.isSubmitting ||
                          isSubmitting ||
                          form.getValues().integration !== Integrators.shopware
                        }
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Only enabled for Shop-Ware integration
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="statusesToSync"
                render={({ field }) => {
                  const integration = form.watch("integration");
                  const statuses =
                    STATUS_OPTIONS[
                      integration as keyof typeof STATUS_OPTIONS
                    ] || [];

                  const handleStatusClick = (statusValue: string) => {
                    const currentStatuses = field.value || [];
                    const newStatuses = currentStatuses.includes(statusValue)
                      ? currentStatuses.filter((s) => s !== statusValue)
                      : [...currentStatuses, statusValue];
                    field.onChange(newStatuses);
                  };

                  return (
                    <FormItem>
                      <FormLabel>Statuses to Sync</FormLabel>
                      <FormDescription>
                        Select which statuses to import from{" "}
                        {integration
                          ? IntegratorsLabel[
                              integration as keyof typeof IntegratorsLabel
                            ]
                          : "the integration"}
                      </FormDescription>
                      <FormControl>
                        <div className="flex flex-col gap-2">
                          {statuses.map((status, index) => (
                            <CheckboxInput
                              key={`${status.value}-${status.label}-${index}`}
                              value={`${status.value}`}
                              aria-label={status.label}
                              label={status.label}
                              checked={field.value?.includes(`${status.value}`)}
                              onClick={() =>
                                handleStatusClick(`${status.value}`)
                              }
                              className={"border-primary"}
                            />
                          ))}
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
            </div>
          </CardContent>
        </Card>

        {/* Ownership & Billing Card */}
        <Card>
          <CardHeader>
            <CardTitle>Ownership & Billing</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-2">
              <FormField
                control={form.control}
                name="ownerId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Owner ID</FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        placeholder="No owner ID set"
                        disabled
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Read-only legacy field. To change who owns this shop, use
                      Reassign owner above — it also updates the owner's actual
                      access.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-2 gap-2">
                <FormField
                  control={form.control}
                  name="stripeCustomerId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Stripe Customer ID</FormLabel>
                      <FormControl>
                        <Input
                          type="text"
                          placeholder="cus_..."
                          disabled={form.formState.isSubmitting || isSubmitting}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="stripeSubscriptionId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Stripe Subscription ID</FormLabel>
                      <FormControl>
                        <Input
                          type="text"
                          placeholder="sub_..."
                          disabled={form.formState.isSubmitting || isSubmitting}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="stripeSubscriptionStatus"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Stripe Subscription Status</FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        disabled={true}
                        className="bg-muted"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Read-only field showing current subscription status
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </CardContent>
        </Card>

        {/* Branding Card */}
        {initialData?.id && (
          <Card>
            <CardHeader>
              <CardTitle>Branding</CardTitle>
            </CardHeader>
            <CardContent>
              <FormField
                control={form.control}
                name="logoUrl"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Shop Logo</FormLabel>
                    <FormControl>
                      <Logo
                        shopId={initialData.id}
                        logoUrl={field.value || ""}
                        canBeChanged
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>
        )}

        {/* Danger Zone Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-destructive">Danger Zone</CardTitle>
          </CardHeader>
          <CardContent className="rounded-lg bg-destructive/20 p-4">
            <FormField
              control={form.control}
              name="disabled"
              render={({ field }) => (
                <FormItem className="flex items-center gap-2">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onClick={() => {
                        field.onChange(!field.value);
                      }}
                      disabled={form.formState.isSubmitting || isSubmitting}
                    />
                  </FormControl>
                  <FormLabel>Disable Shop</FormLabel>
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex flex-row justify-end gap-4">
          <Button type="button" variant="secondary" onClick={handleCancel}>
            {cancelTitle ?? "Cancel"}
          </Button>
          <Button
            type="submit"
            variant="accent"
            disabled={
              !form.formState.isValid ||
              form.formState.isSubmitting ||
              isSubmitting
            }
          >
            {isSubmitting ? "Submitting..." : (submitTitle ?? "Create")}
          </Button>
        </div>
      </form>
    </Form>
  );
};
